<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <h2
        v-if="fields?.title?.value"
        class="flow-chart-bio-cell-progress-title"
        v-html="fields?.title?.value"
      />
      <div
        class="flow-chart-bio-cell-progress-steps-swiper swiper"
        ref="stepsSwiperEl"
        v-if="fields?.steps.length > 0"
      >
        <div class="swiper-wrapper">
          <div
            v-if="showConnector"
            class="flow-chart-bio-cell-progress-steps-connector"
            :style="connectorStyle"
          ></div>
          <site-link
            v-for="(step, index) in fields?.steps"
            :key="index"
            class="swiper-slide flow-chart-bio-cell-progress-steps-slide"
            :link="{
              value: {
                href: getContactHref(step?.fields),
                linktype: 'internal',
              },
            }"
            @click="handleClick($event, step?.fields)"
          >
            <div class="flow-chart-bio-cell-progress-steps-step">
              <JssRichText
                class="flow-chart-bio-cell-progress-steps-step-number"
                :field="step?.fields?.step"
                :style="{ backgroundColor: step?.fields?.stepTagColor?.value }"
              />
              <JssRichText
                class="flow-chart-bio-cell-progress-steps-step-title"
                :field="step?.fields?.stepName"
                tag="h3"
              />
            </div>
          </site-link>
        </div>
      </div>
      <div
        ref="imageWrapperRef"
        class="flow-chart-bio-cell-progress-image-wrapper"
        v-if="fields?.image"
      >
        <AdaptiveImage
          class="flow-chart-bio-cell-progress-image"
          :field="fields?.image"
          :is-background="false"
          :show="true"
          :enable-scroll-detection="false"
          :show-image-redius="false"
        />
      </div>
      <div
        class="flow-chart-bio-cell-progress-cards-swiper swiper"
        ref="cardsSwiperEl"
        v-if="fields?.steps?.length > 0 && !fields?.hideCard?.value"
      >
        <div class="swiper-wrapper">
          <div
            v-for="(card, index) in fields?.steps"
            :key="index"
            class="swiper-slide flow-chart-bio-cell-progress-cards-slide"
          >
            <div class="flow-chart-bio-cell-progress-cards-card">
              <JssRichText
                class="flow-chart-bio-cell-progress-cards-card-title"
                :field="card?.fields?.stepTitle"
                tag="h3"
              />
              <JssRichText
                class="flow-chart-bio-cell-progress-cards-card-subtitle"
                :field="card?.fields?.stepSubtitle"
              />
              <JssRichText
                class="flow-chart-bio-cell-progress-cards-card-tag"
                :field="card?.fields?.stepTag"
              />
              <div
                class="flow-chart-bio-cell-progress-cards-card-desc"
                v-if="card?.fields?.stepDesc?.value"
              >
                <JssRichText :field="card?.fields?.stepDesc" />
              </div>
              <div
                class="flow-chart-bio-cell-progress-cards-card-button"
                @click="openModal(card)"
                v-if="card?.fields?.button"
              >
                <siteButton v-bind="card?.fields?.button" :open-link="false" />
              </div>
            </div>
          </div>
        </div>
        <JssRichText class="flow-chart-bio-cell-progress-desc" :field="fields?.desc" />
      </div>
    </ContentGrid>

    <!-- 弹窗 -->
    <Teleport to="body">
      <transition name="flow-chart-bio-cell-progress-fade">
        <div
          v-if="modalVisible"
          class="flow-chart-bio-cell-progress-modal-mask"
          @click.self="closeModal"
        >
          <div class="flow-chart-bio-cell-progress-modal">
            <span class="flow-chart-bio-cell-progress-modal-close" @click="closeModal"
              >&times;</span
            >
            <div class="flow-chart-bio-cell-progress-modal-body">
              <JssRichText :field="modalContent" />
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import { computed, ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import Swiper from 'swiper';
import { destroySwiper } from '@/utils/site-utils';
import 'swiper/css';
import utils from '@/utils';
import AdaptiveImage from '@/elements/AdaptiveImage.vue';

export default {
  name: 'FlowChartBioCellProgress',
  components: {
    AdaptiveImage,
  },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
  },
  setup(props) {
    const stepsSwiperEl = ref(null);
    const cardsSwiperEl = ref(null);
    const imageWrapperRef = ref(null);
    const connectorStyle = ref({});
    const modalVisible = ref(false);
    const modalContent = ref(null);
    let stepsSwiper = null;
    let cardsSwiper = null;
    let isSyncing = false;

    const desktopSlidesPerView = computed(() => {
      const len = props.fields?.steps?.length || 0;
      return len > 0 && len < 4 ? len : 3;
    });

    const stepsSwiperOptions = computed(() => ({
      slidesPerView: 1.12,
      spaceBetween: 16,
      breakpoints: {
        1024: {
          slidesPerView: desktopSlidesPerView.value,
          spaceBetween: 20,
        },
      },
      on: {
        slideChange: (swiper) => {
          onSlideChange(swiper);
        },
      },
    }));

    const cardsSwiperOptions = computed(() => ({
      slidesPerView: 1,
      spaceBetween: 20,
      breakpoints: {
        1024: {
          slidesPerView: desktopSlidesPerView.value,
          spaceBetween: 20,
        },
      },
      on: {
        slideChange: (swiper) => {
          onSlideChange(swiper);
        },
      },
    }));

    const onSlideChange = (swiper) => {
      if (isSyncing) return;
      isSyncing = true;

      const activeIndex = swiper.activeIndex;

      // 同步两个swiper
      if (stepsSwiper && stepsSwiper.activeIndex !== activeIndex) {
        stepsSwiper.slideTo(activeIndex);
      }
      if (cardsSwiper && cardsSwiper.activeIndex !== activeIndex) {
        cardsSwiper.slideTo(activeIndex);
      }

      // 同步图片滚动
      if (imageWrapperRef.value) {
        const imageWidth = imageWrapperRef.value.scrollWidth - imageWrapperRef.value.clientWidth;
        const scrollPosition = (imageWidth / (props.fields.steps?.length - 1)) * activeIndex;
        imageWrapperRef.value.scrollTo({
          left: scrollPosition,
          behavior: 'smooth',
        });
      }

      setTimeout(() => {
        isSyncing = false;
      }, 300);
    };

    const initSwiper = () => {
      destroySwiper(stepsSwiper, true, true);
      destroySwiper(cardsSwiper, true, true);

      if (stepsSwiperEl.value) {
        stepsSwiper = new Swiper(stepsSwiperEl.value, stepsSwiperOptions.value);
      }

      if (cardsSwiperEl.value) {
        cardsSwiper = new Swiper(cardsSwiperEl.value, cardsSwiperOptions.value);
      }

      updateConnectorDimensions();
      equalizeCardRowHeights();
    };

    /**
     * 让卡片内同一行元素（title/subtitle/tag）跨卡片高度一致，
     * 以内容最多的那个为参照，不截断文案，保证 desc 顶部水平对齐。
     */
    const equalizeCardRowHeights = () => {
      if (!cardsSwiperEl.value) return;
      const rowSelectors = [
        '.flow-chart-bio-cell-progress-cards-card-title',
        '.flow-chart-bio-cell-progress-cards-card-subtitle',
        '.flow-chart-bio-cell-progress-cards-card-tag',
      ];
      rowSelectors.forEach((selector) => {
        const items = cardsSwiperEl.value.querySelectorAll(selector);
        if (items.length === 0) return;

        // 先重置为 auto，获取自然高度
        items.forEach((el) => {
          el.style.height = 'auto';
        });

        // 取最大高度
        let maxHeight = 0;
        items.forEach((el) => {
          const h = el.offsetHeight;
          if (h > maxHeight) maxHeight = h;
        });

        // 统一应用最大高度
        if (maxHeight > 0) {
          items.forEach((el) => {
            el.style.height = `${maxHeight}px`;
          });
        }
      });
    };

    const updateConnectorDimensions = () => {
      if (!stepsSwiperEl.value || !showConnector.value) return;

      const slides = stepsSwiperEl.value.querySelectorAll('.swiper-slide');
      if (slides.length === 0) return;

      const firstSlide = slides[0];
      const lastSlide = slides[slides.length - 1];

      const left = firstSlide.offsetLeft + firstSlide.offsetWidth / 2 - 60;
      const right = lastSlide.offsetLeft + lastSlide.offsetWidth / 2 + 60;

      connectorStyle.value = {
        left: `${left}px`,
        width: `${right - left}px`,
      };
    };

    const handleResize = () => {
      nextTick(() => {
        updateConnectorDimensions();
        equalizeCardRowHeights();
      });
    };

    onMounted(() => {
      window.addEventListener('resize', handleResize);
      // eslint-disable-next-line vue/valid-next-tick
      setTimeout(() => nextTick(() => initSwiper()), 50);
      // 字体加载完成后重新等高，避免字体加载导致的高度变化
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          equalizeCardRowHeights();
        });
      }
    });

    watch(
      () => props.fields.steps?.length,
      () => {
        nextTick(() => initSwiper());
      }
    );

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize);
      destroySwiper(stepsSwiper, true, true);
      destroySwiper(cardsSwiper, true, true);
    });

    const rootClass = computed(() =>
      utils.overall.baseComClass(props.fields, 'flow-chart-bio-cell-progress')
    );

    const showConnector = computed(() => {
      const steps = props.fields?.steps;
      if (!steps || steps.length === 0) return false;
      return steps.every((step) => step?.fields?.step?.value);
    });

    const rootStyle = computed(() => {
      const result = utils.overall.baseComStyle(props.fields);
      return result;
    });

    const getContactHref = (stepFields) => {
      const href = stepFields?.link?.value?.href;
      if (!href) return href;
      const contactId = stepFields?.contactId?.value;
      if (!contactId) return href;
      return href.includes('#') ? href : `${href}#${contactId}`;
    };

    const handleClick = (e, stepFields) => {
      const contactId = stepFields?.contactId?.value;
      if (!contactId) return;
      const href = stepFields?.link?.value?.href;
      if (href) return;
      e.preventDefault();
      e.stopPropagation();
      const anchor = document.getElementById(contactId);
      if (anchor) {
        const offset = anchor.getBoundingClientRect();
        window.scrollTo(0, window.scrollY + offset.top - 80);
      }
    };

    // 弹窗
    const openModal = (card) => {
      if (!card?.fields?.button?.fields?.openModal?.value) return;
      modalContent.value = card?.fields?.modalBody;
      modalVisible.value = true;
    };

    const closeModal = () => {
      modalVisible.value = false;
      modalContent.value = null;
    };

    return {
      stepsSwiperEl,
      cardsSwiperEl,
      imageWrapperRef,
      rootClass,
      rootStyle,
      connectorStyle,
      showConnector,
      getContactHref,
      handleClick,
      modalVisible,
      modalContent,
      openModal,
      closeModal,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.flow-chart-bio-cell-progress {
  position: relative;
  width: 100%;
  padding: 40px 0;

  &-title {
    margin-bottom: 32px;
    text-align: center;
  }

  &-desc {
    margin-top: 24px;
    font-size: 14px;
  }

  &-steps {
    &-swiper {
      overflow: hidden;
      margin-bottom: 24px;
      position: relative;
    }

    &-connector {
      position: absolute;
      top: 18px;
      height: 2px;
      background-color: $blue-night;
      pointer-events: none;
    }
    &-step {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 20px;

      &-number {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-color: $blue-night;
        color: $white;
        font-size: 14px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        position: relative;
        z-index: 2;
      }

      &-title {
        font-size: 16px;
        font-weight: 700;
        color: $black;
        cursor: pointer;
      }
    }
  }

  &-image-wrapper {
    margin-bottom: 32px;
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &-image {
    width: calc((100vw - 24px) * 3);
    height: auto;
  }

  &-cards {
    &-swiper {
      overflow: hidden;
      background-color: #f6faf9;
      padding: 16px;
      margin-top: 16px;
    }

    &-slide {
      height: auto;
      display: flex;
    }

    &-card {
      background: $white;
      border-radius: 8px;
      padding: 20px;
      display: flex;
      flex: 1;
      min-width: 0;
      flex-direction: column;

      &-title {
        font-size: 18px;
        font-weight: 700;
        color: $black;
        margin-bottom: 8px;
      }

      &-subtitle {
        font-size: 14px;
        color: $color-growth-green;
        font-weight: 600;
        margin-bottom: 16px;
      }

      &-tag {
        font-size: 14px;
        line-height: 1.2;
        color: $grey-black;
        margin-bottom: 20px;
        font-weight: 700;
      }

      &-desc {
        font-size: 14px;
        line-height: 1.6;
      }

      &-button {
        margin-top: auto;
        padding-top: 16px;
        min-height: 48px;
      }
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-title {
      margin-bottom: 48px;
    }

    &-steps {
      &-swiper {
        margin: 0 auto;
        width: grid-width(16);
      }

      &-step {
        flex-direction: column;
        gap: 24px;

        &-number {
          font-size: 14px;
        }

        &-title {
          font-size: 18px;
          white-space: nowrap;
        }
      }
    }

    &-image-wrapper {
      margin: 40px auto 0;
      width: grid-width(20);
    }

    &-image {
      width: 100%;
      height: auto;
    }

    &-cards {
      &-swiper {
        padding: 30px;
        border-radius: 16px;
      }

      &-card {
        background: $white;
        border-radius: 16px;
        padding: 24px;
        display: flex;
        flex-direction: column;

        &-title {
          font-size: 18px;
          line-height: 1.2;
          font-weight: 700;
          color: $grey-black;
          margin-bottom: 16px;
        }

        &-subtitle {
          font-size: 18px;
          line-height: 1.2;
          color: $blue-night;
          font-weight: 600;
          margin-bottom: 20px;
        }

        &-desc {
          padding: 16px 12px;
          background-color: rgba($blue-night, 0.1);
          border-radius: 8px;
          font-size: 14px;
          line-height: 1.2;
          color: $grey-black;
        }
      }
    }
  }

  // 弹窗
  &-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 80vw;
    max-height: 60vh;
    background: $white;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    z-index: 1001;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    &-mask {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: 1000;
    }

    &-close {
      position: absolute;
      top: 12px;
      right: 16px;
      font-size: 28px;
      line-height: 1;
      cursor: pointer;
      color: #999;
      z-index: 1;

      &:hover {
        color: #333;
      }
    }

    &-body {
      padding: 32px;
      overflow-y: auto;
      font-size: 16px;
      line-height: 1.6;
      color: #364153;
    }
  }
}

// 弹窗过渡动画
.flow-chart-bio-cell-progress-fade {
  &-enter-active,
  &-leave-active {
    transition: opacity 0.3s;

    .flow-chart-bio-cell-progress-modal {
      transition: transform 0.3s;
    }
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;

    .flow-chart-bio-cell-progress-modal {
      transform: translate(-50%, -50%) scale(0.9);
    }
  }
}
</style>
